import { useApiKey } from '@/stores/apiKey.store';
import { ArrowRight, KeyRound, Zap, Code2, Sparkles, UserPlus, BookOpen } from 'lucide-react';

const ENABLE_URL = 'https://satutim.id/settings/api?return=playground';
const REGISTER_URL = 'https://satutim.id/register?return=playground';

export default function GettingStartedPage() {
    const { key } = useApiKey();

    return (
        <div className="max-w-3xl mx-auto px-6 lg:px-8 py-10">
            {/* Breadcrumb-style eyebrow */}
            <div className="flex items-center gap-1.5 text-xs mb-4" style={{ color: 'var(--ghost)' }}>
                <BookOpen className="h-3 w-3" />
                <span>Docs</span>
                <span>/</span>
                <span style={{ color: 'var(--muted)' }}>Getting Started</span>
            </div>

            <h1 className="text-3xl font-semibold tracking-tight mb-3" style={{ color: 'var(--ink)' }}>
                SatuTim API Playground
            </h1>
            <p className="text-base leading-relaxed mb-8" style={{ color: 'var(--muted)' }}>
                Eksplorasi endpoint, coba langsung dari browser, generate code sample multi-bahasa,
                dan integrasikan dengan AI assistant favoritmu (Claude, ChatGPT, MCP).
                {!key && <> <strong style={{ color: 'var(--ink)' }}>Login dulu (gratis)</strong> untuk dapet API key.</>}
            </p>

            {/* Auth status — primary CTA, GitBook callout style */}
            <section
                className="mb-10 p-5 rounded-lg"
                style={{
                    backgroundColor: key ? 'var(--accent-bg)' : 'var(--surface-1)',
                    border: `1px solid ${key ? 'var(--accent-border)' : 'var(--border)'}`,
                }}
            >
                <div className="flex items-start gap-3">
                    <div
                        className="h-9 w-9 rounded-md flex items-center justify-center shrink-0"
                        style={{
                            backgroundColor: key ? 'var(--accent-border)' : 'var(--surface-2)',
                            color: key ? 'var(--accent-2)' : 'var(--muted)',
                        }}
                    >
                        <KeyRound className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h2 className="text-sm font-semibold mb-1" style={{ color: 'var(--ink)' }}>
                            {key ? 'API key aktif' : 'Login untuk lanjut'}
                        </h2>
                        <p className="text-sm leading-relaxed mb-3" style={{ color: 'var(--muted)' }}>
                            {key
                                ? 'Kamu sudah terhubung. Fitur "Coba" di setiap endpoint akan pakai key kamu otomatis.'
                                : 'Playground ini gratis untuk semua user terdaftar. Login (atau daftar 30 detik), aktifkan API key dari pengaturan, dan kamu langsung bisa pakai semua fitur di sini.'}
                        </p>
                        {!key && (
                            <div className="flex flex-wrap gap-2">
                                <a
                                    href={ENABLE_URL}
                                    className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md transition-opacity hover:opacity-90"
                                    style={{ backgroundColor: 'var(--accent)', color: 'var(--on-accent)' }}
                                >
                                    Login & aktifkan API <ArrowRight className="h-3.5 w-3.5" />
                                </a>
                                <a
                                    href={REGISTER_URL}
                                    className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md transition-colors"
                                    style={{
                                        backgroundColor: 'var(--bg)',
                                        color: 'var(--ink)',
                                        border: '1px solid var(--border)',
                                    }}
                                >
                                    <UserPlus className="h-3.5 w-3.5" /> Daftar gratis
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Features grid — GitBook style: clean cards, subtle borders */}
            <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--ink)' }}>Fitur</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-10">
                <FeatureCard
                    icon={<Code2 className="h-4 w-4" />}
                    title="Try-it interaktif"
                    description="Kirim request langsung dari browser, lihat response real-time."
                />
                <FeatureCard
                    icon={<Sparkles className="h-4 w-4" />}
                    title="Ask AI inline"
                    description="Pertanyaan endpoint → jawaban kontekstual. Atau buka di Claude/ChatGPT."
                />
                <FeatureCard
                    icon={<Zap className="h-4 w-4" />}
                    title="MCP ready"
                    description="Copy MCP config, paste di Claude Desktop atau Cursor. Selesai."
                />
            </div>

            <div
                className="rounded-lg p-4"
                style={{
                    backgroundColor: 'var(--surface-1)',
                    border: '1px solid var(--border)',
                }}
            >
                <p className="text-sm" style={{ color: 'var(--muted)' }}>
                    💡 <strong style={{ color: 'var(--ink)' }}>Tip:</strong>{' '}
                    Pilih endpoint di sidebar kiri untuk mulai eksplorasi.{' '}
                    <kbd className="font-mono text-xs px-1.5 py-0.5 rounded mx-0.5" style={{
                        backgroundColor: 'var(--bg)',
                        border: '1px solid var(--border)',
                        color: 'var(--ink)',
                    }}>⌘K</kbd>
                    {' '}untuk pencarian cepat.
                </p>
            </div>
        </div>
    );
}

function FeatureCard({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
    return (
        <div
            className="p-4 rounded-lg gb-card-hover"
            style={{
                backgroundColor: 'var(--bg)',
                border: '1px solid var(--border)',
            }}
        >
            <div
                className="h-8 w-8 rounded-md flex items-center justify-center mb-3"
                style={{
                    backgroundColor: 'var(--accent-bg)',
                    color: 'var(--accent)',
                }}
            >
                {icon}
            </div>
            <h3 className="text-sm font-semibold mb-1" style={{ color: 'var(--ink)' }}>{title}</h3>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--muted)' }}>{description}</p>
        </div>
    );
}
